/**
 * AuthorshipWarningModal — shown before KDP export when chapters are below
 * the admin-set human-authorship threshold.
 *
 * Enforcement = warn_only (confirmed): author always gets an "Export anyway"
 * button. The modal lists each non-compliant / warning chapter with specific
 * paragraph counts so the author knows exactly what to rewrite.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AuthorshipMeter } from '@/components/ui/AuthorshipMeter';
import { ShieldAlert, ShieldCheck, Download, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onOpenChange
 * @param {string}   props.projectId
 * @param {function} props.onConfirmExport  — called when user clicks "Export anyway"
 */
export function AuthorshipWarningModal({ open, onOpenChange, projectId, onConfirmExport }) {
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    axios.post(`${API}/projects/${projectId}/authorship/validate-export`, {}, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const allClear = data?.all_clear;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0" data-testid="authorship-warning-modal">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            {allClear
              ? <ShieldCheck className="h-4 w-4 text-emerald-500" />
              : <ShieldAlert className="h-4 w-4 text-amber-500" />
            }
            Authorship Compliance Check
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {allClear
              ? 'All chapters meet the human-authorship standard.'
              : `Some chapters are below the ${data?.min_pct ?? 30}% human-authorship threshold.`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto px-5 py-4 space-y-3 text-[12px]">
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking authorship…
            </div>
          )}

          {!loading && data && (
            <>
              {/* Overall score */}
              <div className="flex items-center gap-3 rounded-md bg-panel-header/40 px-3 py-2.5">
                <span className="text-muted-foreground">Project overall:</span>
                <AuthorshipMeter
                  pct={data.overall_human_pct}
                  minPct={data.min_pct}
                  warnPct={data.warn_pct}
                  hasBaseline={true}
                  size="md"
                />
              </div>

              {/* Non-compliant chapters */}
              {data.below_min?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-3 w-3" /> Below minimum ({data.min_pct}%)
                  </div>
                  {data.below_min.map((ch) => (
                    <ChapterRow key={ch.chapter_id} ch={ch} minPct={data.min_pct} warnPct={data.warn_pct} />
                  ))}
                </div>
              )}

              {/* Warning chapters */}
              {data.below_warn?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                    <ShieldAlert className="h-3 w-3" /> Near the threshold ({data.min_pct}%–{data.warn_pct}%)
                  </div>
                  {data.below_warn.map((ch) => (
                    <ChapterRow key={ch.chapter_id} ch={ch} minPct={data.min_pct} warnPct={data.warn_pct} />
                  ))}
                </div>
              )}

              {/* No baseline chapters */}
              {data.no_baseline?.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {data.no_baseline.length} chapter(s) have no AI baseline — they were hand-written or pre-imported. These are treated as fully human.
                </p>
              )}

              {allClear && (
                <div className="flex items-center gap-2 rounded-md border border-emerald-400/40 bg-emerald-50/60 dark:bg-emerald-900/20 px-3 py-2.5 text-emerald-700 dark:text-emerald-300">
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  All chapters meet the {data.min_pct}% standard. The authorship certificate will be included in your ZIP.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-border px-5 py-3 gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => { onOpenChange(false); onConfirmExport?.(); }}
            data-testid="authorship-warning-export-btn"
            className={cn(!allClear && 'bg-amber-500 hover:bg-amber-600 text-white')}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {allClear ? 'Download KDP ZIP' : 'Export anyway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChapterRow({ ch, minPct, warnPct }) {
  const needed = minPct - ch.human_pct;
  const paras_to_rewrite = ch.total_paragraphs > 0
    ? Math.max(1, Math.ceil((minPct / 100) * ch.total_paragraphs) - ch.human_paragraphs)
    : 0;

  return (
    <div
      className="flex flex-wrap items-start justify-between gap-2 rounded-sm border border-border bg-background px-3 py-2"
      data-testid={`authorship-chapter-row-${ch.chapter_number}`}
    >
      <div>
        <span className="font-medium">Ch. {ch.chapter_number}</span>
        {ch.chapter_title && <span className="ml-1.5 text-muted-foreground">{ch.chapter_title}</span>}
        {ch.human_pct < minPct && paras_to_rewrite > 0 && (
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            Rewrite any <span className="font-semibold text-foreground">{paras_to_rewrite}</span> AI paragraph{paras_to_rewrite !== 1 ? 's' : ''} to reach {minPct}%
          </div>
        )}
      </div>
      <AuthorshipMeter pct={ch.human_pct} minPct={minPct} warnPct={warnPct} hasBaseline={ch.has_baseline} size="xs" />
    </div>
  );
}
